import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Button as MuiButton,
  Box,
  Link,
} from "@mui/material";
import priceMapButton from "@/assets/ChikaMap_M.gif";
import {
  Button,
  ButtonVariantOption,
  ButtonColorOption,
} from "@/components/Button";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";
import { useIsMobile } from "@/hooks/useIsMobile";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import FmdGoodIcon from "@mui/icons-material/FmdGood";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { MIN_TAP_TARGET, MOBILE_BREAKPOINT } from "@/styles/responsive";

interface OtherButtonModalProps {
  open: boolean;
  onClose: () => void;
  // 操作ボタン用のprops
  kaokuNumberSearchFlag?: boolean;
  handleKaokuNumberSearch?: () => void;
  realEstateDocumentFetchFlag?: boolean;
  handleRealEstateDocumentFetchModal?: () => void;
  handlePinSave?: () => void;
  handleNewPropertySheet?: () => void;
  propertySummary?: boolean;
  handleSetInitialPosition?: () => Promise<void> | void;
  isSavingInitialPosition?: boolean;
  // Google Maps（衛星写真）のリンク先
  googleMapsUrl?: string;
  // 地価マップのリンク先
  chikaMapUrl?: string;
}

/** 別のページへ移るリンク。アイコンと文字を並べ、SP では指で押せる高さを取る */
const externalLinkSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  minHeight: { xs: MIN_TAP_TARGET, [MOBILE_BREAKPOINT]: "auto" },
} as const;

/**
 * ポップアップの操作メニューを表示するモーダル
 * 地番・家屋番号検索、役所資料取得依頼、物件概要書生成、ピン保存などのボタンを配置
 */
export const OtherButtonModal: React.FC<OtherButtonModalProps> = ({
  open,
  onClose,
  kaokuNumberSearchFlag = false,
  handleKaokuNumberSearch,
  realEstateDocumentFetchFlag = false,
  handleRealEstateDocumentFetchModal,
  handlePinSave,
  handleNewPropertySheet,
  propertySummary = false,
  handleSetInitialPosition,
  isSavingInitialPosition = false,
  googleMapsUrl,
  chikaMapUrl,
}) => {
  const [isInitialPositionProcessing, setInitialPositionProcessing] =
    useState(false);
  const isReadOnly = useReadOnlyUser();
  const isMobile = useIsMobile();

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // 誤タップで閉じない。閉じるのは「閉じる」ボタンだけ
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: { minHeight: "200px" },
      }}
    >
      <DialogTitle>
        操作メニュー
        <MuiButton
          variant="outlined"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            minWidth: "auto",
            padding: "4px 12px",
            fontSize: "0.875rem",
          }}
        >
          閉じる
        </MuiButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {kaokuNumberSearchFlag && handleKaokuNumberSearch && (
            <Grid item xs={12}>
              <Button
                label={"地番・家屋番号検索"}
                onClick={() => {
                  handleKaokuNumberSearch();
                  onClose();
                }}
                size={SizingWrapperStyle.INHERIT}
                variant={ButtonVariantOption.Outlined}
                color={ButtonColorOption.Primary}
              />
            </Grid>
          )}
          {realEstateDocumentFetchFlag &&
            handleRealEstateDocumentFetchModal && (
              <Grid item xs={12}>
                <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth>
                  <Button
                    label={"役所資料取得依頼"}
                    onClick={() => {
                      handleRealEstateDocumentFetchModal();
                      onClose();
                    }}
                    size={SizingWrapperStyle.INHERIT}
                    variant={ButtonVariantOption.Outlined}
                    color={ButtonColorOption.Primary}
                    disabled={isReadOnly}
                  />
                </ReadOnlyTooltip>
              </Grid>
            )}
          {handlePinSave && (
            <Grid item xs={12}>
              <Button
                label={"ピンを保存"}
                onClick={() => {
                  handlePinSave();
                  onClose();
                }}
                size={SizingWrapperStyle.INHERIT}
                variant={ButtonVariantOption.Outlined}
                color={ButtonColorOption.Primary}
              />
            </Grid>
          )}
          {handleNewPropertySheet && propertySummary && (
            <Grid item xs={12}>
              <ReadOnlyTooltip isReadOnly={isReadOnly} fullWidth>
                <Button
                  label={"物件概要書を生成"}
                  onClick={() => {
                    handleNewPropertySheet();
                    onClose();
                  }}
                  size={SizingWrapperStyle.INHERIT}
                  variant={ButtonVariantOption.Outlined}
                  color={ButtonColorOption.Primary}
                  disabled={isReadOnly}
                />
              </ReadOnlyTooltip>
            </Grid>
          )}
          {handleSetInitialPosition && (
            <Grid item xs={12}>
              <Button
                label={"初期位置に設定"}
                onClick={() => {
                  void (async () => {
                    if (
                      isSavingInitialPosition ||
                      isInitialPositionProcessing
                    ) {
                      return;
                    }
                    setInitialPositionProcessing(true);
                    try {
                      await handleSetInitialPosition();
                      onClose();
                    } finally {
                      setInitialPositionProcessing(false);
                    }
                  })();
                }}
                size={SizingWrapperStyle.INHERIT}
                variant={ButtonVariantOption.Outlined}
                color={ButtonColorOption.Primary}
                hasLoading
                loading={isSavingInitialPosition || isInitialPositionProcessing}
                disabled={
                  isSavingInitialPosition || isInitialPositionProcessing
                }
              />
            </Grid>
          )}
          {/* 別のページへ移るものは、ボタンでなくリンクにする（このモーダルの操作と見分けられるように） */}
          {(googleMapsUrl !== undefined || chikaMapUrl !== undefined) && (
            <Grid item xs={12}>
              <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 3 }}>
                {googleMapsUrl && (
                  <Link
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    onClick={onClose}
                    sx={externalLinkSx}
                  >
                    <FmdGoodIcon fontSize="small" />
                    Google Maps（衛星写真）
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                )}
                {chikaMapUrl && (
                  <Link
                    href={chikaMapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    onClick={onClose}
                    sx={externalLinkSx}
                  >
                    <Box
                      component="img"
                      src={priceMapButton}
                      alt=""
                      sx={{ height: 18 }}
                    />
                    全国地価マップ
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </Link>
                )}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
